import { useEffect } from 'react';
import { Link } from 'react-router-dom';

/* ── icons ───────────────────────────────────────────────────────────── */

function IconArrowRight() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
    </svg>
  );
}

function IconBolt() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
    </svg>
  );
}

function IconPlug() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
    </svg>
  );
}

function IconLightbulb() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
    </svg>
  );
}

function IconDocument() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}

function IconLoop() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
    </svg>
  );
}

/* ── building blocks ─────────────────────────────────────────────────── */

function FeatureCard({ icon, title, description, to }) {
  const Wrapper = to ? Link : 'div';
  return (
    <Wrapper
      to={to}
      className="block rounded-xl border p-5 border-gray-200 bg-gray-50/50 dark:border-brand-50/10 dark:bg-brand-800/40 hover:border-gray-300 dark:hover:border-brand-50/20 transition-colors"
    >
      <div className="flex items-center gap-2.5 mb-2 text-gray-700 dark:text-brand-200">
        {icon}
        <h3 className="font-semibold text-[15px] m-0">{title}</h3>
      </div>
      <p className="text-[14px] leading-relaxed text-gray-500 dark:text-brand-500 m-0">{description}</p>
    </Wrapper>
  );
}

function StatCard({ number, label }) {
  return (
    <div className="rounded-xl border p-5 border-gray-200 bg-gray-50/50 dark:border-brand-50/10 dark:bg-brand-800/40 text-center">
      <p className="text-3xl font-bold tracking-tighter mb-1 text-gray-800 dark:text-brand-100">{number}</p>
      <p className="text-[13px] leading-snug text-gray-500 dark:text-brand-500 m-0">{label}</p>
    </div>
  );
}

/* ── main component ──────────────────────────────────────────────────── */

export default function WhatIsPabs({ onHeadingsExtracted }) {
  useEffect(() => {
    if (onHeadingsExtracted) {
      onHeadingsExtracted([
        { text: 'The problem', level: 2, id: 'the-problem' },
        { text: 'What PABS does', level: 2, id: 'what-pabs-does' },
        { text: 'How it works', level: 2, id: 'how-it-works' },
        { text: 'The trust model', level: 2, id: 'the-trust-model' },
        { text: 'Who it\'s for', level: 2, id: 'whos-its-for' },
      ]);
    }
  }, [onHeadingsExtracted]);

  return (
    <article className="min-w-0 max-w-[680px]">

      {/* Hero */}
      <h1 className="text-3xl font-bold tracking-tighter mt-0 mb-3 leading-tight text-gray-900 dark:text-brand-50">
        What is PABS?
      </h1>
      <p className="text-[17px] leading-relaxed mb-3 text-gray-500 dark:text-brand-400">
        <strong className="font-semibold text-gray-700 dark:text-brand-300">Living strategy for living products.</strong>
      </p>
      <p className="text-[15px] leading-relaxed mb-10 text-gray-600 dark:text-brand-400">
        PABS is the strategic layer between your idea and your build stack. Tell it your challenge, get a full strategy in minutes - then keep it connected to the tools your team builds with every day.
      </p>

      {/* The problem */}
      <h2 id="the-problem" className="text-xl font-semibold tracking-tight mb-5 leading-snug scroll-mt-20 text-gray-900 dark:text-brand-50">
        The problem
      </h2>
      <p className="text-[15px] leading-relaxed mb-5 text-gray-600 dark:text-brand-400">
        AI made execution fast. Cursor, Replit, Claude, and Lovable can ship working code in hours. But speed without direction isn't progress - it's expensive mistakes in real time.
      </p>
      <div className="grid grid-cols-2 gap-4 mb-5">
        <StatCard number="80%" label="of shipped features go unused" />
        <StatCard number="35–42%" label="of startups fail due to lack of market need" />
      </div>
      <p className="text-[15px] leading-relaxed mb-10 text-gray-600 dark:text-brand-400">
        AI compounds this: the faster you build in the wrong direction, the more you waste. PABS makes sure your speed has a target.
      </p>

      {/* What PABS does */}
      <h2 id="what-pabs-does" className="text-xl font-semibold tracking-tight mb-5 leading-snug scroll-mt-20 text-gray-900 dark:text-brand-50">
        What PABS does
      </h2>
      <div className="grid grid-cols-1 gap-4 mb-10">
        <FeatureCard
          icon={<IconBolt />}
          title="AI-powered strategy generation"
          description="Submit a brief and get a complete strategy in minutes - hypotheses, competitive positioning, action plan, decisions, and design vision. Not weeks. Minutes."
        />
        <FeatureCard
          icon={<IconPlug />}
          title="Live connection to your build tools"
          to="/docs/integrations-overview"
          description="PABS connects to Cursor, Claude, Figma, and more via MCP. Your tools read your live strategy directly - no copy-paste, no stale docs, no context loss."
        />
        <FeatureCard
          icon={<IconBolt />}
          title="Jobs & routines"
          to="/docs/jobs-and-routines"
          description="Run product-management work on demand, or set routines that keep checking the strategy as your product context changes."
        />
        <FeatureCard
          icon={<IconLightbulb />}
          title="Learnings Engine"
          description="Captures real-world observations from builders, research sessions, or your build tools, and proposes targeted strategy updates for your approval. Strategy evolves with evidence."
        />
        <FeatureCard
          icon={<IconDocument />}
          title="Living strategic artefacts"
          description="Generate PRDs, SOWs, READMEs, and competitor analyses - grounded in your live strategy. Stakeholders get direction. AI tools get context. Zero translation loss."
        />
      </div>

      {/* How it works */}
      <h2 id="how-it-works" className="text-xl font-semibold tracking-tight mb-5 leading-snug scroll-mt-20 text-gray-900 dark:text-brand-50">
        How it works
      </h2>
      <p className="text-[15px] leading-relaxed mb-4 text-gray-600 dark:text-brand-400">
        <strong className="font-semibold text-gray-800 dark:text-brand-200">PABS is the product manager you don't have.</strong> You hand it your idea once. After that it stays with you while you build, quietly making sure what you're shipping is still the right thing.
      </p>
      <p className="text-[15px] leading-relaxed mb-6 text-gray-600 dark:text-brand-400">
        It works as one continuous loop, not a checklist you finish:
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <FeatureCard
          icon={<IconDocument />}
          title="It knows your plan"
          description="What “right” looks like for your product, held in one place."
        />
        <FeatureCard
          icon={<IconPlug />}
          title="It watches you build"
          description="Connected to the tools you already use, so it sees what's actually happening."
        />
        <FeatureCard
          icon={<IconLightbulb />}
          title="It speaks up when it counts"
          description="It surfaces the one decision or risk worth your attention now. You make the call. The plan updates."
        />
      </div>

      <div className="flex items-center gap-2 mb-6 text-[13px] text-gray-500 dark:text-brand-500">
        <IconLoop />
        <span>Then it goes back to watching. The loop never stops.</span>
      </div>

      <p className="text-[15px] leading-relaxed mb-6 text-gray-600 dark:text-brand-400">
        You never have to sit down and “manage the strategy.” PABS does that in the background. You just keep building and making the calls only you can make.
      </p>

      {/* Getting started footnote - the one-time setup, kept small next to the forever loop */}
      <div className="rounded-xl border border-gray-200 bg-gray-50/50 dark:border-brand-50/10 dark:bg-brand-800/40 p-4 mb-10">
        <p className="text-[13px] font-semibold text-gray-700 dark:text-brand-300 mb-1">
          Getting started: two things and you're set
        </p>
        <p className="text-[13px] leading-relaxed text-gray-500 dark:text-brand-500 m-0">
          Submit a brief in plain language, then connect your build tools. PABS runs the loop from there.
        </p>
      </div>

      {/* Trust model */}
      <h2 id="the-trust-model" className="text-xl font-semibold tracking-tight mb-4 leading-snug scroll-mt-20 text-gray-900 dark:text-brand-50">
        The trust model
      </h2>
      <div className="rounded-xl border border-gray-200 bg-gray-50/50 dark:border-brand-50/10 dark:bg-brand-800/40 p-5 mb-10">
        <p className="text-[15px] leading-relaxed text-gray-600 dark:text-brand-400 m-0">
          <strong className="font-semibold text-gray-800 dark:text-brand-200">AI evaluates and proposes. Human approves and decides.</strong> The strategy only changes after you make a decision. PABS will never update your strategy without your sign-off.{' '}
          <Link to="/docs/trust-model" className="underline underline-offset-2 text-gray-600 dark:text-brand-400 hover:text-gray-900 dark:hover:text-brand-100 transition-colors">
            Read more about the trust model →
          </Link>
        </p>
      </div>

      {/* Who it's for */}
      <h2 id="whos-its-for" className="text-xl font-semibold tracking-tight mb-4 leading-snug scroll-mt-20 text-gray-900 dark:text-brand-50">
        Who it's for
      </h2>
      <p className="text-[15px] leading-relaxed mb-8 text-gray-600 dark:text-brand-400">
        PABS is built for <strong className="font-semibold text-gray-800 dark:text-brand-200">founders, product leads, and design strategists</strong> who are building with AI and need strategy to keep up with execution velocity. If you're using vibe coding tools and making real product decisions, PABS is your strategic layer.
      </p>

      {/* CTA */}
      <div className="rounded-xl border border-gray-200 bg-gray-50/50 dark:border-brand-50/10 dark:bg-brand-800/40 p-5 flex items-center justify-between gap-4">
        <div>
          <p className="font-semibold text-[15px] text-gray-800 dark:text-brand-200 mb-0.5">Ready to try it?</p>
          <p className="text-[13px] text-gray-500 dark:text-brand-500 m-0">From idea to live strategy in under five minutes.</p>
        </div>
        <Link
          to="/docs/create-your-first-plan"
          className="inline-flex items-center gap-2 shrink-0 rounded-lg px-4 py-2 text-[13px] font-semibold transition-colors
            bg-gray-900 text-white hover:bg-gray-700
            dark:bg-brand-50 dark:text-brand-900 dark:hover:bg-brand-200"
        >
          Quick Start <IconArrowRight />
        </Link>
      </div>

    </article>
  );
}
